import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { Auth } from '@/components/public/auth/Auth'

type InvitationInfo = {
  active: boolean
  brand_name: string | null
  brand_id?: string
  access_level?: string
  email?: string
  message?: string
  expires_at?: string
}

const AcceptInvitation = () => {
  console.log('[AcceptInvitation Debug] Component rendering')
  const [searchParams] = useSearchParams()
  const params = useParams()
  // Check for token in URL params first (from /invitations/brand/:token), then query params
  const tokenFromPath = params.token || ''
  const tokenFromQuery = searchParams.get('token') || ''
  const initialToken = tokenFromPath || tokenFromQuery
  console.log('[AcceptInvitation Debug] Token from path:', tokenFromPath, 'Token from query:', tokenFromQuery)
  
  const [token, setToken] = useState<string>(initialToken)
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [userSession, setUserSession] = useState<any>(null)
  const [authTab, setAuthTab] = useState<string>('signup')
  console.log(authTab);
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const lookupInvitation = useCallback(async (token: string) => {
    console.log('[AcceptInvitation Debug] Looking up invitation with token:', token)
    setLoading(true)
    try {
      // For lookups, we don't need authentication
      const { data, error } = await supabase.rpc('lookup_brand_invitation', {
        lookup_token: token
      })

      if (error) {
        console.error('[AcceptInvitation Debug] Error looking up invitation:', error)
        toast({
          title: 'Error',
          description: 'Failed to look up invitation. ' + error.message,
          variant: 'destructive'
        })
        return
      }

      console.log('[AcceptInvitation Debug] Invitation lookup result:', data)
      setInvitationInfo(data as InvitationInfo)
    } catch (error) {
      console.error('[AcceptInvitation Debug] Error in lookupInvitation:', error)
    } finally {
      setLoading(false)
    }
  }, [toast])
  
  // Check if user is logged in
  useEffect(() => {
    const checkSession = async () => {
      console.log('[AcceptInvitation Debug] Checking session')
      const { data } = await supabase.auth.getSession()
      console.log('[AcceptInvitation Debug] Session data:', data)
      setUserSession(data.session)
      
      // If we have a session and token, automatically look up the invitation
      if (data.session && token) {
        console.log('[AcceptInvitation Debug] Have session and token, looking up invitation')
        lookupInvitation(token)
      }
    }
    
    checkSession()
    
    // Set up listener for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('[AcceptInvitation Debug] Auth state changed:', _event)
      setUserSession(session)
      
      // If a user just signed in and we have a token, look up the invitation again
      if (session && token) {
        console.log('[AcceptInvitation Debug] New session detected with token, looking up invitation')
        lookupInvitation(token)
      }
    })
    
    return () => {
      console.log('[AcceptInvitation Debug] Cleaning up auth listener')
      authListener.subscription.unsubscribe()
    }
  }, [token, lookupInvitation])

  // If token is provided in URL, look up the invitation details
  useEffect(() => {
    console.log('[AcceptInvitation Debug] Token effect running, token:', token)
    if (token) {
      lookupInvitation(token)
    }
  }, [token, lookupInvitation])

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToken(e.target.value)
    setInvitationInfo(null) // Clear previous lookup results when token changes
  }

  const acceptInvitation = async () => {
    console.log('[AcceptInvitation Debug] Starting invitation acceptance process')
    if (!invitationInfo?.active) {
      console.log('[AcceptInvitation Debug] Invitation is not active')
      toast({
        title: 'Error',
        description: 'This invitation is not valid or has expired.',
        variant: 'destructive'
      })
      return
    }

    // Check if user is authenticated
    if (!userSession) {
      console.log('[AcceptInvitation Debug] User is not authenticated')
      toast({
        title: 'Authentication Required',
        description: 'Please sign in or create an account to accept this invitation.',
        variant: 'default'
      })
      setAuthTab('login')
      return
    }

    console.log('[AcceptInvitation Debug] User authenticated, current user:', userSession.user.id)
    setLoading(true)
    try {
      console.log('[AcceptInvitation Debug] Calling accept_brand_invitation RPC with token:', token)
      const { data, error } = await supabase.rpc('accept_brand_invitation', {
        invitation_token: token
      })
      
      console.log('[AcceptInvitation Debug] RPC response:', { data, error })

      if (error) {
        console.error('Error accepting invitation:', error)
        toast({
          title: 'Error',
          description: 'Failed to accept invitation. ' + error.message,
          variant: 'destructive'
        })
        return
      }
      
      // Log successful data
      console.log('[AcceptInvitation Debug] Invitation accepted successfully:', data)

      toast({
        title: 'Success',
        description: `You have been added to ${invitationInfo.brand_name}. You can now access all team resources.`,
        duration: 5000
      })

      // Short delay to show the success message before redirect
      setTimeout(() => {
        // Redirect to brands page directly rather than onboarding since we'll auto-set onboarding_completed
        navigate('/brands')
      }, 1500)
      
    } catch (error) {
      console.error('Error in acceptInvitation:', error)
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex pt-16">
      <Card className="mx-auto w-full max-w-md">
        <CardHeader>
          <CardTitle>Join Your Team</CardTitle>
          <CardDescription>
            {token ? 'Accept an invitation to join a team' : 'Enter your invitation token'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!userSession ? (
            <div className="space-y-4">
              {/* Display invitation details if we have a token */}
              {invitationInfo && (
                <div className="rounded-md border bg-muted/50 p-4">
                  <h3 className="font-medium">Invitation Details</h3>
                  {invitationInfo.active ? (
                    <div className="mt-2">
                      <p>You've been invited to join {invitationInfo.brand_name}</p>
                      <p className="mt-1 text-sm text-green-600">This invitation is active</p>
                      <p className="mt-2 text-sm">Please sign in or create an account to accept this invitation.</p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-red-600">
                      This invitation is not valid or has expired
                    </p>
                  )}
                </div>
              )}

              {/* Token input for users who don't have a token yet */}
              {!invitationInfo && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="token">Invitation Token</Label>
                    <Input
                      id="token"
                      placeholder="Enter your invitation token"
                      value={token}
                      onChange={handleTokenChange}
                    />
                  </div>
                  {token && !loading && (
                    <Button
                      variant="outline"
                      onClick={() => lookupInvitation(token)}
                      disabled={!token || loading}
                      className="w-full"
                    >
                      Verify Token
                    </Button>
                  )}
                </div>
              )}

              {/* Authentication tabs for unauthenticated users */}
              {invitationInfo?.active && (
                <Auth 
                  view="invite" 
                  redirectTo={`/process-invitation?invitation_token=${token}`}
                  hideLabels 
                />
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* For authenticated users, show invitation details and accept button */}
              <div className="space-y-2">
                <Label htmlFor="token">Invitation Token</Label>
                <Input
                  id="token"
                  placeholder="Enter your invitation token"
                  value={token}
                  onChange={handleTokenChange}
                />
              </div>

              {token && !loading && !invitationInfo && (
                <Button
                  variant="outline"
                  onClick={() => lookupInvitation(token)}
                  disabled={!token || loading}
                  className="w-full"
                >
                  Verify Token
                </Button>
              )}

              {invitationInfo && (
                <div className="p-4 rounded-md border mt-4">
                  <h3 className="font-medium">Invitation Details</h3>
                  {invitationInfo.active ? (
                    <div className="mt-2">
                      <p>You've been invited to join {invitationInfo.brand_name}</p>
                      <p className="text-sm text-green-600 mt-1">This invitation is active</p>
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 mt-2">
                      This invitation is not valid or has expired
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end">
          {userSession && (
            <Button
              onClick={acceptInvitation}
              disabled={loading || !invitationInfo?.active}
              className="w-full"
            >
              {loading ? 'Processing...' : 'Accept Invitation'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}

export default AcceptInvitation